import { useCookies } from 'react-cookie';
import { useCallback, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';

interface CookieOptions {
  path?: string;
  expires?: Date;

  [key: string]: any;
}

type UseCookieReturnType<T = string> = [
  T | undefined,
  (value: T, options?: CookieOptions) => void,
  (options?: CookieOptions) => void,
];

export const useCookie = <T = string>(
  key: string,
  defaultOptions: CookieOptions = {}, // Default options
  defaultValue?: T,
): UseCookieReturnType<T> => {
  const [cookies, setCookie, removeCookie] = useCookies([key]);

  useEffect(() => {
    if (!cookies[key] && typeof defaultValue !== 'undefined')
      setCookie(key, defaultValue, defaultOptions);
  }, []);

  const updateCookieFunction = useCallback(
    (value: T, options: CookieOptions = {}) => {
      const { expires, ...restOptions } = { ...defaultOptions, ...options };

      setCookie(key, value, {
        path: '/',
        expires: expires ? new Date(expires) : undefined,
        ...restOptions,
      });
    },
    [key, defaultOptions, setCookie],
  );

  const debouncedUpdateCookie = useDebouncedCallback(updateCookieFunction, 150);

  const cookieValue = useMemo(() => {
    try {
      switch (cookies[key]) {
        case typeof undefined:
          return undefined;
        default:
          if (cookies[key] === 'null') return null;
          return cookies[key] as T;
      }
    } catch {
      return undefined;
    }
  }, [cookies, key]);

  const updateCookie = useCallback(
    (value: T, options: CookieOptions = {}) => {
      debouncedUpdateCookie(value, options);
    },
    [debouncedUpdateCookie],
  );

  const deleteCookie = useCallback(
    (options: CookieOptions = {}) => {
      removeCookie(key, {
        path: '/',
        ...options,
      });
    },
    [key, removeCookie],
  );

  return useMemo(
    () => [cookieValue, updateCookie, deleteCookie],
    [cookieValue, updateCookie, deleteCookie],
  );
};